import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from "react-redux";
import { useHistory } from 'react-router-dom';

import ReactTable from "react-table";
import "react-table/react-table.css";

import {
  loadSingleSitesListRefreshStart,
  loadSingleSitesListRefreshCancel,
} from "services/redux/actions/singleSites";
import { loadSiteViewLiveRefreshCancel } from "services/redux/actions";

import { createGetEventMetaById } from "services/redux/selectors/eventMeta";
import { getCustomRef } from "services/redux/selectors/groups";
import { getSingleSitesList } from "services/redux/selectors/singleSites";

import { LatestAlarm, SiteName, SiteStatus } from "components/Enterprise/Overview/SiteList/Cells/";

import FilterSelect from "components/Common/Dropdown/FilterSelect";

import SiteView from "components/Site/SiteView/";

import { contains, exact } from "services/filters/";

import Icon from "components/Icons/Icon";

import Loading from "components/Loading";

import { withT } from "services/translation/";

import isEmpty from "lodash/isEmpty";

const COLORS = {
  setBlue: "#28498A",
  unsetLightblue: "#3397DB",
  noStateMidBlue: "#2B3E4E",
};

function DataTable(props) {
  const { t, getEventMetaById } = props;
  const dispatch = useDispatch();
  const history = useHistory();

  const [showSidePane, setShowSidePane] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [bundleId, setBundleId] = useState("");

  const sites = useSelector(getSingleSitesList);
  const timeframe = useSelector(state => state.sites.all.singleSites.timeframe || 24);
  const loadingSingleSites = useSelector(state => state.sites.all.singleSites.loadingSingleSites);

  useEffect(() => {
    dispatch(
      loadSingleSitesListRefreshCancel()
    );

    dispatch(
      loadSingleSitesListRefreshStart({ timeframe })
    );

    return () => {
      dispatch(
        loadSingleSitesListRefreshCancel()
      );
    }
  }, [timeframe]);

  const mediaType = props.browser.mediaType;

  let hideOrShowCol = null;
  let unset = { whiteSpace: "unset" };
  let set = {};
  let width = null;
  let styleResponsive = null;
  let widthMobile = 70;
  let widthLarge = 100;

  if (mediaType === "extraSmall") {
    hideOrShowCol = false;
    styleResponsive = unset;
    width = widthMobile;
  } else if (
    mediaType === "medium" ||
    mediaType === "large" ||
    mediaType === "infinity"
  ) {
    hideOrShowCol = true;
    styleResponsive = set;
    width = widthLarge;
  } else if (mediaType === "small") {
    hideOrShowCol = false;
    styleResponsive = set;
    width = widthLarge;
  }

  const onSelectSite = site => {
    if (site && site !== selectedSite) {
      // New site selected
      document.body.classList.add("sidepane-active");

      setShowSidePane(true);
      setSelectedSite(site);
      setBundleId("");

      history.push(`/sites/${site.id}/${bundleId}`);
      
    } else {
      // Either the user has clicked a site that's already being viewed, or has clicked to close the side pane
      document.body.classList.remove("sidepane-active");

      dispatch(
        loadSiteViewLiveRefreshCancel()
      );

      // Delay by 600ms to make sidepane exit gracefully
      setTimeout(() => {
        setShowSidePane(false);
        setSelectedSite(null);
        setBundleId("");

        history.push(`/sites/`);
      }, 600);
    }
  };

  const columns = [
    {
      key: "SITE_NAME",
      Header: t("headers.site_name"),
      accessor: "site_name",
      id: "site_name",
      className: "site-name",
      Cell: ({ original }) => {
        const custom_ref = getCustomRef(original);

        return <SiteName custom_ref={custom_ref} value={original.name} />;
      },
    },
    {
      key: "SITE_STATUS",
      Header: t("headers.site_status"),
      headerClassName: "wordwrap",
      id: "SiteStatus",
      accessor: "zoneState.state",
      className: "site-status bg-white",
      width: width,
      Cell: (props) => (
        <SiteStatus
          {...props}
          original={{
            ...props.original,
            open_close: "N", // this bit is not relevant here - just make it N by default
          }}
          onChangeSiteSet={(params) => {
          }}
        />
      ),
      filterMethod: (filter, row) => {
        return exact({
          filter: filter.value,
          value: String(row[filter.id]),
          ignoreCase: true,
        });
      },
    },
    {
      Header: t("headers.latest_alarm"),
      headerClassName: "wordwrap",
      id: "LatestAlarm",
      className: "latest-alarm bg-white text-dark",
      width: width,
      accessor: (row) => {
        const alarmLast = isEmpty(row.alarmLast) ? {} : row.alarmLast;
        return {
          ...alarmLast,
          ...getEventMetaById(row.last_alarm_id)
        };
      },
      Cell: (props) => <LatestAlarm {...props} t={t} />,
      Filter: ({ filter, onChange, column }) => {
        let collectedIds = {};
        const options = sites
          .map((site) => {
            if (!site.last_alarm_id) return null;
            const id = site.last_alarm_id;
            const event = getEventMetaById(id);

            if (collectedIds.hasOwnProperty(id)) {
              return null;
            }
            collectedIds[id] = true;
            return {
              name: event.caption,
              color: event.colour,
              id,
              icon: event.icon,
            };
          })
          .filter((i) => i);
        options.unshift({
          name: "With conversation",
          color: "#777777",
          id: -1,
          icon: "comment"
        });

        return (
          <FilterSelect
            onChange={onChange}
            value={filter ? filter.value : ""}
            options={options}
          />
        );
      },
      filterMethod: (filter, row) => {
        if (filter.value === "With conversation") {
          return row.LatestAlarm && row.LatestAlarm.linkedCommentCount && (row.LatestAlarm.linkedCommentCount > 0);
        } else {
          const alarm = row[filter.id];

          return (
            contains({
              filter: filter.value,
              value: alarm.caption,
              ignoreCase: true,
            }) ||
            contains({
              filter: filter.value,
              value: alarm.alarmDescription || "",
              ignoreCase: false,
            })
          );
        }
      },
      sortMethod: ({ acked: a }, { acked: b }) => {
        // put sites that don't yet have alarms (i.e. no acked) below sites that do have alarms
        if (!a && !b) return 0;
        if (!a && b) return 1;
        if (a && !b) return -1;

        // otherwise order by acked time
        return new Date(a).getTime() < new Date(b).getTime() ? 1 : -1;
      },
    },
    {
      Header: t("headers.alarm_count"),
      id: "alarmCount",
      // accessor: "alarmCount",
      accessor: (data) => {
        return {
          alarmCount: data.alarm_count,
          alarmLast: data.latest_alarm,
        }
      },
      show: hideOrShowCol,
      className: "alarm-count bg-white text-dark",
      width: 100,
      Cell: ({ value }) => {
        return (
          <span className="d-block">
            <div
              className="text-center d-inline-block"
              style={{ width: "60%" }}
            >
              <Icon className="far fa-bell px-2" />
            </div>
            <div
              className="text-center d-inline-block"
              style={{ width: "50%" }}
            >
              {value.alarmCount}
            </div>
          </span>
        );
      },
      filterMethod: (filter, row) => {
        const alarmCount = row[filter.id];

        return exact({
          filter: filter.value,
          value: String(alarmCount.alarmCount),
          ignoreCase: true,
        });
      },
      sortMethod: (a, b) => {
        // put sites that don't yet have alarms (i.e. no .alarmLast) above sites that do have alarms
        if (!a.alarmLast && !b.alarmLast) return 0;
        if (!a.alarmLast && b.alarmLast) return -1;
        if (a.alarmLast && !b.alarmLast) return 1;

        return a.alarmCount - b.alarmCount;
      }
    }
  ];
  const loadingMsg = loadingSingleSites ? <Loading center /> : "No data available...";

  return (
    <div>
      <div className="card visual-data site-list">
        <ReactTable
          columns={columns}
          data={sites}
          filterable
          defaultFilterMethod={(filter, row) => {
            return (
              String(row[filter.id])
                .toLowerCase()
                .indexOf(filter.value.toLowerCase()) !== -1
            );
          }}
          defaultPageSize={20}
          showPagination={false}
          noDataText={loadingMsg}
          defaultSorted={[
            {
              id: "LatestAlarm",
              desc: false,
            },
          ]}
          getTrGroupProps={(state, rowInfo, column) => {
            if (!rowInfo) return { className: "empty-row" };

            const site = sites[rowInfo.index];

            const selected =
              site && selectedSite && site.id === selectedSite.id
                ? "selected"
                : "";

            const colorKey = rowInfo.original.zoneState.colour;

            let color = COLORS[colorKey] || "#666666";

            return {
              className: [colorKey, selected, "waves-effect"].join(" "),
              style: {
                background: color,
                border: "1px solid " + color,
              },
              onClick: () => {
                onSelectSite(rowInfo.original)
              },
            };
          }}
        />
      </div>
      <SiteView
        origin="SingleSite"
        site={selectedSite}
        selectSite={onSelectSite}
        bundle_id={bundleId}
        showSidePane={showSidePane}
        onSelectBundle={bundleId => {
          // Bundle selected
          if (bundleId) {
            setBundleId(bundleId);
            history.push(`/sites/${selectedSite.id}/${bundleId}`);
          }
          // Invalid bundle id or deselect
          else {
            setBundleId("");
            history.push(`/sites/${selectedSite.id}`);
          }
        }}
      />
    </div>
  );
}

const mapStateToProps = (state, props) => {
  return {
    browser: state.browser,
    getEventMetaById: createGetEventMetaById(state, props),
  };
};

export default withT(connect(mapStateToProps)(DataTable),"site_list.data_table");